import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, Outlet, useLocation } from 'react-router-dom';
// dayjs
import * as en from 'dayjs/locale/en';
import * as ja from 'dayjs/locale/ja';
import dayjs from 'utils/day';
// actions
import { refreshTokenAction as refreshToken } from 'redux/actions/auth';
// layouts
import AppLayout from 'layout/main';
import AuthenticationLayout from 'layout/authentication';
// components
import Loading from 'components/Loading';
import Error404 from 'routes/error/Error404';
import Error401 from 'routes/error/Error401';
import Error403 from 'routes/error/Error403';
import PrivacyPolicy from './policy/policy';
import Overview from 'routes/overview';
import Signin from 'routes/auth/Signin';
import ForgotPassword from 'routes/auth/ForgotPassword';
import CheckEmail from 'routes/auth/CheckEmail';
import ResetPassword from 'routes/auth/ResetPassword';
import ResetPasswordResult from 'routes/auth/ResetPasswordResult';
import TeacherPage from 'routes/teacher/TeacherPage';
import TeacherView from 'routes/teacher/TeacherView';
import AddTeacher from 'routes/teacher/AddTeacher';
import EditTeacher from 'routes/teacher/EditTeacher';
import StudentPage from 'routes/student/StudentPage';
import StudentView from 'routes/student/StudentView';
import AddStudent from 'routes/student/AddStudent';
import EditStudent from 'routes/student/EditStudent';
import ClassPage from 'routes/class/ClassPage';
import AddClass from 'routes/class/AddClass';
import EditClass from 'routes/class/EditClass';
import ClassListStudent from './class/ClassListStudent';

import KanjiPage from './kanji/KanjiPage';
import AddKanji from './kanji/AddKanji';
import EditKanji from './kanji/EditKanji';
import ProfilePage from 'routes/profile/ProfilePage';
import Dashboard from './dashboard/Dashboard';

const ProtectedRoute = ({ children, role }) => {
    const location = useLocation();
    const authUser = useSelector((state) => state.auth.authUser);

    if (authUser) {
        if (role) {
            if (authUser.role === role) return children;
            else return <Navigate to="/403" state={{ from: location }} replace />;
        }

        return children;
    }

    return <Navigate to="/signin" state={{ from: location }} replace />;
};

const AppRoutes = () => {
    const [loading, setLoading] = useState(true);
    const [loadingLanguage, setLoadingLanguage] = useState(true);
    const authUser = useSelector((state) => state.auth.authUser);
    const language = useSelector((state) => state.config.currentLanguage);

    const dispatch = useDispatch();

    useEffect(() => {
        async function getData() {
            try {
                await dispatch(refreshToken());
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        }

        getData();
    }, []);

    useEffect(() => {
        setLoadingLanguage(true);
        if (language === 'en') {
            dayjs.locale({ ...en, weekStart: 1 });
        } else {
            dayjs.locale({ ...ja, weekStart: 1 });
        }

        setTimeout(() => {
            setLoadingLanguage(false);
        }, 100);
    }, [language]);

    if (loading || loadingLanguage) return <Loading />;

    return (
        <Routes>
            {/* Error pages */}
            <Route path="*" element={<Error404 />} />
            <Route exact path="/401" element={<Error401 />} />
            <Route exact path="/403" element={<Error403 />} />
            <Route exact path="/404" element={<Error404 />} />
            <Route exact path="/policy" element={<PrivacyPolicy />} />
            {/* Authentication pages */}
            <Route
                element={
                    <AuthenticationLayout>
                        <Outlet />
                    </AuthenticationLayout>
                }
            >
                <Route exact path="/signin" element={<Signin />} />
                <Route exact path="/forgot-password" element={<ForgotPassword />} />
                <Route exact path="/check-email" element={<CheckEmail />} />
                <Route exact path="/reset-password" element={<ResetPassword />} />
                <Route exact path="/reset-password/result" element={<ResetPasswordResult />} />
            </Route>
            {/* Protected pages */}
            {/* Common routes */}
            <Route
                element={
                    <ProtectedRoute>
                        <AppLayout>
                            <Outlet />
                        </AppLayout>
                    </ProtectedRoute>
                }
            >
                <Route
                    exact
                    path="/"
                    element={authUser?.role === 1 ? <Navigate to="/dashboard" /> : <Navigate to="/students" />}
                />
                <Route exact path="/overview" element={<Overview />} />
                <Route exact path="/profile" element={<ProfilePage />} />
                <Route exact path="/students" element={<StudentPage />} />
                <Route exact path="/students/:id/view" element={<StudentView />} />
                <Route exact path="/classes" element={<ClassPage />} />
                <Route exact path="/classes/:id/list" element={<ClassListStudent />} />
                <Route exact path="/materials" element={<KanjiPage />} />
            </Route>
            {/* Admin routes */}
            <Route
                element={
                    <ProtectedRoute role={1}>
                        <AppLayout>
                            <Outlet />
                        </AppLayout>
                    </ProtectedRoute>
                }
            >
                <Route exact path="/dashboard" element={<Dashboard />} />
                <Route exact path="/teachers" element={<TeacherPage />} />
                <Route exact path="/teachers/:id/view" element={<TeacherView />} />
                <Route exact path="/teachers/create" element={<AddTeacher />} />
                <Route exact path="/teachers/:id/edit" element={<EditTeacher />} />
                <Route exact path="/students/create" element={<AddStudent />} />
                <Route exact path="/students/:id/edit" element={<EditStudent />} />
                <Route exact path="/classes/create" element={<AddClass />} />
                <Route exact path="/classes/:id/edit" element={<EditClass />} />
                <Route exact path="/materials/create" element={<AddKanji />} />
                <Route exact path="/materials/:id/edit" element={<EditKanji />} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
